import { Download, ExternalLink, FileText, Loader2 } from "lucide-react"
import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { getDownloadUrl } from "@/features/transit/api/transit.api"

interface DocPreviewDialogProps {
  otId: number | string
  // The artifact to preview. Pass everything we need to mint a fresh URL.
  // `docType` is the MIME-style identifier persisted on the backend at
  // upload time; we prefer it over filename sniffing.
  doc: {
    name: string
    storedKey: string
    docType?: string | null
    uploadedAt?: string | null
    uploadedBy?: string | null
  } | null
  onClose: () => void
}

type Kind = "image" | "pdf" | "other"

// Resolve the viewer kind from the persisted MIME type first; fall
// back to filename extension for entries that pre-date `docType`.
function detectKind(doc: { docType?: string | null; name: string }): Kind {
  const mime = doc.docType?.toLowerCase()
  if (mime) {
    if (mime.startsWith("image/")) return "image"
    if (mime === "application/pdf") return "pdf"
  }
  const lower = doc.name.toLowerCase()
  if (/\.(png|jpe?g|webp|gif|bmp|avif)$/.test(lower)) return "image"
  if (lower.endsWith(".pdf")) return "pdf"
  return "other"
}

// Inline previewer — opens a presigned URL and renders it via iframe
// (PDFs) or img (images). Unknown types degrade to a download link.
//
// Mints a fresh URL each open so we never expose stale signed links;
// they're only valid for an hour anyway.
export function DocPreviewDialog({ otId, doc, onClose }: DocPreviewDialogProps) {
  return (
    <Dialog open={!!doc} onOpenChange={(v) => (!v ? onClose() : null)}>
      <DialogContent className="flex h-[95vh] w-[95vw] flex-col gap-3 p-0 sm:max-w-none">
        {doc ? (
          <PreviewBody otId={otId} doc={doc} />
        ) : (
          <DialogHeader className="border-b px-5 py-3">
            <DialogTitle />
          </DialogHeader>
        )}
      </DialogContent>
    </Dialog>
  )
}

function PreviewBody({
  otId,
  doc,
}: {
  otId: number | string
  doc: NonNullable<DocPreviewDialogProps["doc"]>
}) {
  const { t } = useTranslation("transit")

  // useQuery handles the cancellation + caching for us; React Query
  // marks unmounted-component fetches stale automatically.
  const { data: url, isLoading } = useQuery({
    queryKey: ["transit", "preview", otId, doc.storedKey],
    queryFn: () => getDownloadUrl(otId, doc.storedKey).then((r) => r.url),
    staleTime: 30 * 60 * 1000, // signed URLs are valid for 1h
    gcTime: 30 * 60 * 1000,
  })

  const kind = detectKind(doc)

  return (
    <>
      <DialogHeader className="border-b px-5 py-3">
        <DialogTitle className="truncate font-mono text-sm">
          {doc.name}
        </DialogTitle>
        <DialogDescription className="text-xs">
          {doc.uploadedAt ? doc.uploadedAt.slice(0, 10) : "—"}
          {doc.uploadedBy ? <> · {doc.uploadedBy}</> : null}
        </DialogDescription>
      </DialogHeader>

      <div className="bg-muted/30 flex-1 min-h-0 overflow-hidden">
        {isLoading || !url ? (
          <div className="text-muted-foreground flex h-full items-center justify-center gap-2 text-sm">
            <Loader2 className="size-4 animate-spin" />
            {t("preview.loading")}
          </div>
        ) : kind === "image" ? (
          <div className="flex h-full items-center justify-center p-4">
            <img
              src={url}
              alt={doc.name}
              className="max-h-full max-w-full object-contain"
            />
          </div>
        ) : kind === "pdf" ? (
          <iframe
            src={url}
            title={doc.name}
            className="h-full w-full border-0"
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
            <FileText className="text-muted-foreground size-10" />
            <p className="text-sm">{t("preview.unsupported")}</p>
            <Button asChild variant="outline" size="sm">
              <a href={url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="size-4" />
                {t("preview.openExternal")}
              </a>
            </Button>
          </div>
        )}
      </div>

      <div className="border-t flex items-center justify-end gap-2 px-5 py-3">
        {url ? (
          <>
            <Button asChild variant="outline" size="sm">
              <a href={url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="size-4" />
                {t("preview.openExternal")}
              </a>
            </Button>
            <Button asChild size="sm">
              <a href={url} download={doc.name}>
                <Download className="size-4" />
                {t("docCard.download")}
              </a>
            </Button>
          </>
        ) : null}
      </div>
    </>
  )
}
